import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import {AttachInspectionOrderDialog} from "../../component/AttachInspectionOrderDialog";
import AppreciationToolbarButton from "../../component/appreciation/AppreciationToolbarButton";
import {resolveAppreciationId, useAppreciationVisible} from "../../component/appreciation/appreciationHooks";
import {DetachInspectionOrderDialog} from "../../component/DetachInspectionOrderDialog";
import {useCanWriteValuationContent} from "../../hooks/permissionHooks";
import {selectAppreciationHasValueCurried, selectAppreciationIsVisibleCurried} from "../../redux/appreciationsUtil";
import {
    selectNeverBeenFinalized,
    selectPurposePolicy,
    selectReadOnly,
    selectValuationMetadata,
} from "../../redux/persistenceSlice";
import {selectInspectionOrderData} from "../../redux/valueOrdersSelectors";

/**
 * React component for a toolbar in the Inspection Tab of the Object page.
 * @param {string} page provide appreciation page and tab, to calculate key
 * @param {string} tab provide appreciation page and tab, to calculate key
 * @param {boolean} appreciationVisible
 * @param {(boolean) => void} setAppreciationVisible
 * @return {JSX.Element|null}
 */
export default function ObjectInspectionToolbar({page, tab, appreciationVisible, setAppreciationVisible}) {
    const effectiveAppreciationKey = resolveAppreciationId(undefined, page, tab);

    const {t} = useTranslation();

    const appreciationButtonVisible = useSelector(selectAppreciationIsVisibleCurried(effectiveAppreciationKey));
    const hasAppreciationValue = useSelector(selectAppreciationHasValueCurried(effectiveAppreciationKey));
    const readOnly = useSelector(selectReadOnly);
    const neverBeenFinalized = useSelector(selectNeverBeenFinalized);
    const canWriteContent = useCanWriteValuationContent();
    const inspectionOrder = useSelector(selectInspectionOrderData);
    const licenseValueClientId = useSelector(selectValuationMetadata)?.licenseValueClientId;
    // #19481: Value-Orders anhängen/lösen nur bei Zwecken, die weitergegeben werden dürfen (nicht bei Research).
    const {transferAllowed} = useSelector(selectPurposePolicy);
    const [attachInspectionDialogOpen, setAttachInspectionDialogOpen] = useState(false);
    const [detachInspectionVisible, setDetachInspectionVisible] = React.useState(false);

    const appreciationAllowed = useAppreciationVisible(effectiveAppreciationKey);
    const renderAppreciationButton = !(
        !appreciationAllowed ||
        (page && !tab) ||
        (!appreciationButtonVisible && !readOnly) ||
        (readOnly && !hasAppreciationValue)
    );
    // Ohne Client-ID der eigenen Lizenz lehnt der Server das Verknüpfen erst beim Absenden ab
    // (gleicher Grund wie in PanelRevaOrder.jsx, #19302). Verknüpfen und Entkoppeln brauchen wie dort das
    // inhaltliche Schreibrecht im aktuellen Zustand, Entkoppeln zusätzlich eine nie abgeschlossene Bewertung
    // (#19302 V-B8).
    const renderAttachInspectionButton =
        canWriteContent && !inspectionOrder && transferAllowed && !!licenseValueClientId;
    const renderDetachInspectionButton = neverBeenFinalized && canWriteContent && !!inspectionOrder && transferAllowed;

    if (!renderAppreciationButton && !renderAttachInspectionButton && !renderDetachInspectionButton) {
        return null;
    }

    return (
        <div id="objectInspectionToolbar" className="HeaderToolbarRight">
            {renderAppreciationButton && (
                <AppreciationToolbarButton
                    key="appreciationButton"
                    page={page}
                    tab={tab}
                    appreciationVisible={appreciationVisible}
                    setAppreciationVisible={setAppreciationVisible}
                />
            )}

            {renderAttachInspectionButton && (
                <>
                    <Button
                        key="attachInspectionButton"
                        icon={PrimeIcons.RECEIPT}
                        className="ml-1"
                        text
                        tooltip={t("object.tab_inspection.toolbar.attachInspectionOrder")}
                        tooltipOptions={{position: "left"}}
                        onClick={() => setAttachInspectionDialogOpen(true)}
                    />
                    <AttachInspectionOrderDialog
                        visible={attachInspectionDialogOpen}
                        onClose={() => setAttachInspectionDialogOpen(false)}
                    />
                </>
            )}

            {renderDetachInspectionButton && (
                <>
                    <Button
                        key="detachInspectionButton"
                        icon={PrimeIcons.RECEIPT}
                        className="ml-1"
                        severity="danger"
                        text
                        tooltip={t("object.tab_inspection.toolbar.detachInspectionOrder")}
                        tooltipOptions={{position: "left"}}
                        onClick={() => setDetachInspectionVisible(true)}
                    />

                    <DetachInspectionOrderDialog
                        valueOrderGuid={inspectionOrder?.valueOrderGuid}
                        externalOrderNumber={inspectionOrder?.valueExternalOrderNumber}
                        visible={detachInspectionVisible}
                        onClose={() => setDetachInspectionVisible(false)}
                    />
                </>
            )}
        </div>
    );
}
